<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-background-container ffj-declaration-page">
    <div ref="cardRef" class="orangehrm-card-container ffj-monthly-declaration">
      <h6 class="orangehrm-main-title ffj-monthly-declaration__title">
        {{ $t('forfait_jours.monthly_declaration_title') }}
      </h6>

      <!-- Status + transition notice. Sticky on small screens so the
           declaration context stays visible while the days scroll. -->
      <div
        v-if="declaration"
        class="ffj-monthly-declaration__statusbar"
        data-testid="ffj-statusbar"
      >
        <p
          class="ffj-monthly-declaration__subtitle"
          data-testid="ffj-month-year-label"
        >
          {{ monthLabel }} — {{ $t('forfait_jours.status_label') }}:
          <span
            :class="`ffj-status-badge ffj-status-badge--${(
              declaration.status || ''
            ).toLowerCase()}`"
            data-testid="ffj-declaration-status"
          >
            {{
              $t(
                `forfait_jours.status_${(
                  declaration.status || ''
                ).toLowerCase()}`,
              )
            }}
          </span>
        </p>

        <!-- Transition notice: shown only for DRAFT declarations the
             employee can still edit. -->
        <div
          v-if="showTransitionNotice"
          class="ffj-monthly-declaration__transition"
          data-testid="ffj-transition-notice"
        >
          <p class="ffj-monthly-declaration__transition-line1">
            {{
              (declaration.daysUntilTransition ?? 0) <= 1
                ? $t('forfait_jours.transition_in_days_singular', {
                    count: declaration.daysUntilTransition ?? 0,
                    month: nextTransitionMonthName,
                  })
                : $t('forfait_jours.transition_in_days_plural', {
                    count: declaration.daysUntilTransition ?? 0,
                    month: nextTransitionMonthName,
                  })
            }}
          </p>
          <p class="ffj-monthly-declaration__transition-line2">
            {{
              $t('forfait_jours.transition_date', {
                day: nextTransitionDay,
                month: nextTransitionMonthName,
              })
            }}
          </p>
        </div>
      </div>

      <oxd-divider class="ffj-monthly-declaration__divider" />

      <div v-if="isLoading" class="ffj-monthly-declaration__loading">
        {{ $t('general.loading') }}
      </div>
      <div
        v-else-if="loadError"
        class="ffj-monthly-declaration__error"
        role="alert"
      >
        {{ loadError }}
      </div>
      <div v-else-if="declaration" class="ffj-monthly-declaration__body">
        <p v-if="!editable" class="ffj-monthly-declaration__notice">
          {{ $t(`forfait_jours.${declaration.status.toLowerCase()}_notice`) }}
        </p>

        <monthly-grid
          :days="orderedDays"
          :editable="editable"
          @update-day="onUpdateDay"
        />

        <!-- Summary + signature + submit. Sticky at the bottom of the
             viewport on small screens so the submission stays reachable
             without scrolling past every day. -->
        <section
          class="ffj-monthly-declaration__actions"
          data-testid="ffj-actions"
        >
          <oxd-divider class="ffj-monthly-declaration__actions-divider" />

          <section class="ffj-monthly-declaration__summary">
            <div class="ffj-monthly-declaration__summary-cell">
              <span class="ffj-monthly-declaration__summary-label">
                {{ $t('forfait_jours.summary_working_days') }}
              </span>
              <span class="ffj-monthly-declaration__summary-value">
                {{ workingDaysCount }}
              </span>
            </div>
            <div class="ffj-monthly-declaration__summary-cell">
              <span class="ffj-monthly-declaration__summary-label">
                {{ $t('forfait_jours.summary_user_modified') }}
              </span>
              <span class="ffj-monthly-declaration__summary-value">
                {{ userModifiedCount }}
              </span>
            </div>
          </section>

          <oxd-divider class="ffj-monthly-declaration__actions-divider" />

          <section v-if="editable" class="ffj-monthly-declaration__signature">
            <signature-ack-checkbox
              v-model="signatureAcknowledged"
              :disabled="isSubmitting"
              data-testid="ffj-signature-ack"
            />
          </section>

          <oxd-form-actions class="ffj-monthly-declaration__form-actions">
            <oxd-button
              display-type="secondary"
              :label="
                isSubmitting
                  ? $t('general.submitting')
                  : $t('forfait_jours.submit')
              "
              type="button"
              :disabled="!editable || !canSubmit || isSubmitting"
              data-testid="ffj-submit-button"
              @click="onSubmit"
            />
          </oxd-form-actions>

          <p
            v-if="submitError"
            class="ffj-monthly-declaration__error"
            role="alert"
          >
            {{ submitError }}
          </p>

          <!-- PDF download: only visible after manager signs (status SIGNED) -->
          <section
            v-if="declaration?.status === 'SIGNED'"
            data-testid="ffj-signed-pdf-download"
            class="ffj-monthly-declaration__signed-pdf"
          >
            <oxd-button
              display-type="secondary"
              :label="$t('forfait_jours.download_signed_pdf')"
              type="button"
              :disabled="signedPdfLoading"
              @click="onDownloadSignedPdf"
            />
          </section>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useForfaitJours, {
  isWorkingDay,
} from '../util/composable/useForfaitJours';
import MonthlyGrid from '../components/MonthlyGrid.vue';
import SignatureAckCheckbox from '../components/SignatureAckCheckbox.vue';

export default {
  name: 'MyMonthlyDeclaration',
  components: {
    'monthly-grid': MonthlyGrid,
    'signature-ack-checkbox': SignatureAckCheckbox,
  },
  props: {
    declarationId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const comp = useForfaitJours(props.declarationId);
    const submitError = ref(null);
    const signedPdfLoading = ref(false);
    const cardRef = ref(null);

    // The oxd topbar is fixed but its white body actually overflows the
    // 100px wrapper, so the sticky status bar must park below the
    // *measured* topbar bottom instead of a hardcoded offset.
    function syncStickyOffset() {
      const topbarBody = document.querySelector('.oxd-topbar-body');
      if (!cardRef.value || !topbarBody) {
        return;
      }
      const bottom = Math.floor(topbarBody.getBoundingClientRect().bottom);
      if (bottom > 0) {
        cardRef.value.style.setProperty('--ffj-topbar-height', `${bottom}px`);
      }
    }

    // Writable proxy over the composable's signature-ack state so the
    // checkbox v-model writes through to `comp.signatureAcknowledged`.
    // canSubmit reads the same ref, so the submit button enables
    // reactively the moment the user ticks the box.
    const signatureAcknowledged = computed({
      get: () => comp.signatureAcknowledged.value,
      set: (v) => {
        comp.signatureAcknowledged.value = v === true;
      },
    });

    /**
     * Download the signed PDF by fetching the JSON envelope from
     * the API, decoding the base64 content, and triggering a
     * browser download via a Blob URL.
     */
    async function onDownloadSignedPdf() {
      // @ts-expect-error: appGlobal is not in window object by default
      const baseUrl = window.appGlobal.baseUrl;
      const http = new APIService(
        baseUrl,
        '/api/v2/forfait-jours/declarations',
      );
      try {
        const res = await http.get(`${props.declarationId}/pdf`, {
          kind: 'signed',
        });
        const data = res?.data?.data ?? res?.data;
        const b64 = data?.contentBase64;
        const filename = data?.filename ?? 'signed.pdf';
        if (!b64) {
          return;
        }
        const byteChars = atob(b64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        // Silently ignore — the button is only visible when the
        // signed PDF was already confirmed available.
      }
    }

    onMounted(async () => {
      await comp.load();
      // PDF availability is inferred from declaration status (SIGNED).
      // No explicit check needed — the PDF is generated on demand.
    });

    // The mobile oxd layout clips overflow on `.oxd-layout-container`,
    // which prevents `position: sticky` from working for descendants.
    // Tag the body while this page is mounted so the page-scoped global
    // stylesheet (second <style> block) can lift that clipping.
    onMounted(() => {
      document.body.classList.add('ffj-declaration-page-active');
      syncStickyOffset();
      window.addEventListener('resize', syncStickyOffset);
    });
    onBeforeUnmount(() => {
      document.body.classList.remove('ffj-declaration-page-active');
      window.removeEventListener('resize', syncStickyOffset);
    });

    const orderedDays = computed(() => {
      if (!comp.declaration.value) {
        return [];
      }
      return [...comp.declaration.value.days].sort((a, b) => a.day - b.day);
    });

    const editable = computed(() => !!comp.declaration.value?.editable);

    // -----------------------------------------------------------------
    // Transition notice computed properties
    // -----------------------------------------------------------------
    const FRENCH_MONTHS = {
      1: 'janvier',
      2: 'février',
      3: 'mars',
      4: 'avril',
      5: 'mai',
      6: 'juin',
      7: 'juillet',
      8: 'août',
      9: 'septembre',
      10: 'octobre',
      11: 'novembre',
      12: 'décembre',
    };

    /**
     * Parse an ISO 8601 date string and extract the day and French month name.
     */
    function parseTransitionDate(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) {
        return null;
      }
      const day = d.getUTCDate();
      const month = d.getUTCMonth() + 1;
      return {day, monthName: FRENCH_MONTHS[month] ?? '???'};
    }

    const transitionInfo = computed(() => {
      const d = comp.declaration.value;
      if (!d?.nextTransitionDate) {
        return null;
      }
      return parseTransitionDate(d.nextTransitionDate);
    });

    const showTransitionNotice = computed(() => {
      const d = comp.declaration.value;
      if (!d) {
        return false;
      }
      return d.status === 'DRAFT' && d.editable === true;
    });

    const nextTransitionMonthName = computed(() => {
      const info = transitionInfo.value;
      if (!info) {
        return '???';
      }
      return info.monthName;
    });

    const nextTransitionDay = computed(() => {
      const info = transitionInfo.value;
      if (!info) {
        return 0;
      }
      return info.day;
    });

    const monthLabel = computed(() => {
      const d = comp.declaration.value;
      if (!d) {
        return '';
      }
      const mm = String(d.month).padStart(2, '0');
      return `${mm}/${d.year}`;
    });

    const workingDaysCount = computed(() => {
      if (!comp.declaration.value) {
        return 0;
      }
      return comp.declaration.value.days.filter((d) => {
        if (d.nonEligible || d.nonWorking) {
          return false;
        }
        return isWorkingDay(d);
      }).length;
    });

    const userModifiedCount = computed(() => {
      if (!comp.declaration.value) {
        return 0;
      }
      return comp.declaration.value.days.filter((d) => d.source === 'user')
        .length;
    });

    const loadError = computed(() => {
      if (comp.error.value && !comp.isSubmitting.value) {
        return comp.error.value;
      }
      return null;
    });

    const canSubmit = computed(() => comp.canSubmit.value);

    function onUpdateDay({day, patch}) {
      if (typeof patch.dayTypeKey === 'string') {
        comp.setDayField(day, 'dayTypeKey', patch.dayTypeKey);
      }
      if (typeof patch.halfDay === 'boolean') {
        comp.setDayField(day, 'halfDay', patch.halfDay);
      }
      if (typeof patch.restRespected === 'boolean') {
        comp.setDayField(day, 'restRespected', patch.restRespected);
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'comment')) {
        comp.setDayField(day, 'comment', patch.comment);
      }
    }

    async function onSubmit() {
      submitError.value = null;
      await comp.submit();
      if (comp.error.value) {
        submitError.value = comp.error.value;
      }
    }

    return {
      comp,
      cardRef,
      declaration: comp.declaration,
      orderedDays,
      editable,
      monthLabel,
      workingDaysCount,
      userModifiedCount,
      loadError,
      canSubmit,
      signatureAcknowledged,
      submitError,
      isLoading: comp.isLoading,
      isSubmitting: comp.isSubmitting,
      signedPdfLoading,
      onUpdateDay,
      onSubmit,
      onDownloadSignedPdf,
      showTransitionNotice,
      nextTransitionMonthName,
      nextTransitionDay,
    };
  },
};
</script>

<style lang="scss" scoped>
.ffj-monthly-declaration {
  font-family: 'Nunito Sans', sans-serif;
  color: var(--oxd-interface-gray-darken-1-color, #64728c);
  // Height of the fixed oxd topbar; the sticky status bar parks right
  // below it on small screens. Refined at runtime by syncStickyOffset().
  --ffj-topbar-height: 120px;
  &__statusbar {
    margin-bottom: 1rem;
  }
  &__subtitle {
    margin: 0.25rem 0 0;
    color: #475569;
    font-size: 0.875rem;
  }
  &__loading,
  &__error {
    padding: 1rem;
    text-align: center;
    color: #475569;
  }
  &__error {
    color: #b91c1c;
  }
  &__notice {
    background: #fef3c7;
    border-left: 4px solid #f59e0b;
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    margin: 0 0 1rem;
    color: #92400e;
  }
  &__body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &__summary {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
  &__summary-cell {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    background: #f8fafc;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    min-width: 8rem;
  }
  &__summary-label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__summary-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    &--warn {
      color: #b91c1c;
    }
  }
  &__signature {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__guard {
    color: #b91c1c;
    font-size: 0.875rem;
    margin: 0;
  }
  &__signed-pdf {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
  }
  &__transition {
    margin: 0 0 0.75rem;
    padding: 0.5rem 0.75rem;
    background: #eff6ff;
    border-left: 4px solid #3b82f6;
    border-radius: 0.25rem;
  }
  &__transition-line1 {
    margin: 0;
    font-weight: 600;
    font-size: 0.875rem;
    color: #1e40af;
  }
  &__transition-line2 {
    margin: 0.125rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
  }

  // -------------------------------------------------------------------
  // Small screens: the status bar and the submission block stick to the
  // viewport, while the day cards scroll between them.
  // -------------------------------------------------------------------
  @media (max-width: 800px) {
    padding: 1rem;

    &__title {
      margin: 0 0 0.5rem !important;
      font-size: 0.9375rem !important;
    }

    &__statusbar {
      position: sticky;
      top: var(--ffj-topbar-height);
      z-index: 100;
      // Bleed to the card edges so the separator spans its full width.
      margin: 0 -1rem 0.5rem;
      padding: 0.5rem 1rem 0.75rem;
      background: #fff;
      border-bottom: 1px solid #e2e8f0;
    }

    &__subtitle {
      margin: 0;
    }

    &__transition {
      margin: 0.5rem 0 0;
    }

    &__divider {
      display: none;
    }

    &__body {
      gap: 0.5rem;
    }

    &__actions {
      position: sticky;
      bottom: 0;
      z-index: 100;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      // Bleed to the card edges and ride the bottom padding out.
      margin: 0 -1rem -1rem;
      padding: 0.625rem 1rem calc(0.625rem + env(safe-area-inset-bottom, 0px));
      background: #fff;
      border-top: 1px solid #e2e8f0;
      border-radius: 0 0 1.2rem 1.2rem;
    }

    &__actions-divider {
      display: none;
    }

    &__summary {
      gap: 0.5rem;
    }

    &__summary-cell {
      flex: 1 1 0;
      min-width: 0;
      padding: 0.375rem 0.5rem;
    }

    &__summary-label {
      font-size: 0.6875rem;
    }

    &__summary-value {
      font-size: 1.0625rem;
    }

    &__form-actions {
      width: 100%;
      :deep(.oxd-button) {
        width: 100%;
      }
    }

    // Keep a focused comment field clear of the sticky submission block
    // when the mobile keyboard scrolls it into view.
    :deep(.ffj-monthly-grid__comment) {
      scroll-margin-bottom: 12rem;
    }
  }

  // Very short viewports (landscape phones): park the status bar instead
  // of sticking it, otherwise the two sticky bars could overlap and hide
  // the whole day list. Submission stays pinned.
  @media (max-width: 800px) and (max-height: 480px) {
    &__statusbar {
      position: static;
    }
  }
}

.ffj-status-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  &--draft {
    background: #fee2e2;
    color: #991b1b;
  }
  &--submitted {
    background: #fef3c7;
    color: #92400e;
  }
  &--signed {
    background: #d1fae5;
    color: #065f46;
  }
}
</style>

<!--
  Global (unscoped) companion to the scoped block above.

  On small screens the oxd layout wrapper (`.oxd-layout-container`) clips
  overflow, which turns it into a non-scrollable scroll container and
  breaks `position: sticky` for every descendant. The declaration page adds
  `ffj-declaration-page-active` to <body> while mounted (see setup()), so
  only this page lifts the clipping. `width: 100%` compensates for the
  wrapper's `calc(100% + 1px)` width, which the clipping used to hide.
-->
<style lang="scss">
@media (max-width: 800px) {
  body.ffj-declaration-page-active .oxd-layout-container {
    overflow: visible;
    width: 100%;
  }
}
</style>
